import React, { useState, useEffect } from "react";
import { withNamespaces } from "react-i18next";
import {
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Paper,
  Button,
  Stack,
  Menu,
  MenuItem,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Pagination,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Select from "react-select";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import Axios from "axios";
import Sidebar from "../../sidebar/sidebar";
import { companylogo } from "../../../assets/images";
import { BaseUrl } from "../../../Environment";
import moment from "moment";
import { FilterAlt } from "@mui/icons-material";
import Customdaterange from "../../modals/customdaterange/customdaterange";
import { getSecureItem } from "../../../utilsFunctions";
import PdfReportRejection from "./PdfReportRejection";
import { getCompanyProfile } from "../../../api/index";
import excel from "../../../assets/images/excel.svg";
import pdf from "../../../assets/images/pdf.svg";
import { ExcelReport } from "../ExcelReport";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#e1f0fa",
    color: theme.palette.common.black,
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: { fontSize: 14, cursor: "pointer" },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": { backgroundColor: theme.palette.action.hover },
  "&:last-child td, &:last-child th": { border: 0 },
}));

const loanTableHeader = {
  loanId: "Loan Id",
  firstName: "Borrower Name",
  loanAmount: "Lent Amount",
  rejectionDate: "Rejection Date",
  rejectionReason: "Rejection Reason",
  sourceName: "Source Name",
};

const collectionTableHeader = {
  SrNo: "Sr. No.",
  dueDate: "Due Date",
  openingBalance: "Opening Balance",
  Installment: "Installment Amount",
  closingBalance: "Closing Balance",
};

const RejectionReport = () => {
  const loggedinUser = getSecureItem("loggedinUser");
  const columnWidths = [{ wch: 200 }, { wch: 200 }, { wch: 250 }];
  const currency = getSecureItem("country")?.symbol;
  const userType = loggedinUser?.userType || "";

  const fullReportRef = React.useRef(false);
  const [fullReport, setFullReport] = useState(false);
  const setFullReportSync = (val) => {
    fullReportRef.current = val;
    setFullReport(val);
  };

  const [loanData, setLoanData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [sumLoanAmt, setSumLoanAmt] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [gotFullData, setGotFullData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [companyData, setCompanyData] = useState(null);
  const [exportType, setExportType] = useState(null);

  const [loanId, setLoanId] = useState(null);
  const [collectionModal, setCollectionModal] = useState(false);
  const [selectedBorrower, setSelectedBorrower] = useState("");
  const [loanCollectionData, setLoanCollectionData] = useState([]);

  const [page, setPage] = useState(1);
  const [loanTenure, setLoanTenure] = useState("All");
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [branchData, setBranchData] = useState([]);
  const [datePickerM, setDatePickerM] = useState(false);
  const [startDateR, setStartDateR] = useState(null);
  const [endDateR, setEndDateR] = useState(null);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const openFilter = Boolean(anchorElFilter);

  const buildPayload = () => ({
    companyId: getSecureItem("loggedinUserCompany"),
    collectorId: loggedinUser?.userId,
    loanStatus: "Cancelled",
    isRejection: true,
    caFilter: false,
    loanTenureType: loanTenure,
    limit: 20,
    page,
    userType,
    startDateR,
    endDateR,
    fullReport: fullReportRef.current,
    branchId:
      loggedinUser?.userType === "superAdmin"
        ? selectedBranch?.value || null
        : selectedBranch?.value || loggedinUser?.branchId || null,
  });

  const fetchBranches = async () => {
    try {
      const token = getSecureItem("token");
      const { data } = await Axios.get(`${BaseUrl}/branch/getAllBranches`, {
        params: { companyId: loggedinUser.companyId },
        headers: { Authorization: "Bearer " + token },
      });
      const list =
        loggedinUser?.userType === "superAdmin"
          ? data?.data
          : data?.data?.filter((b) => b.branchId === loggedinUser?.branchId);
      setBranchData(
        list?.map((b) => ({ label: b.branchName, value: b.branchId })) || [],
      );
    } catch (err) {
      console.error("Fetch Branches Error:", err);
    }
  };

  const getLoansData = async () => {
    try {
      setLoading(true);
      const token = getSecureItem("token");
      const res = await Axios.post(
        `${BaseUrl}/loan/getLoanReport`,
        buildPayload(),
        { headers: { Authorization: "Bearer " + token } },
      );
      const result = res?.data?.result || [];
      setLoanData(result);
      setSumLoanAmt(
        result.reduce(
          (acc, entry) => acc + parseFloat(entry?.totalAmountToCollect || 0),
          0,
        ),
      );
      setTotalPages(Math.ceil((res?.data?.totalRecords || 0) / 20) || 1);
      if (fullReportRef.current) setGotFullData(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getLoanCollections = async (id) => {
    try {
      const token = getSecureItem("token");
      const { data } = await Axios.get(
        `${BaseUrl}/loanCollection/getLoanCollectionByLoanId/?loanId=${id}`,
        { headers: { Authorization: "Bearer " + token } },
      );
      setLoanCollectionData(data?.data?.loanCollections || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getCompany = async () => {
    try {
      const compData = await getCompanyProfile();
      const {
        profilePic,
        companyName,
        GstIn,
        companyWebsite,
        phone,
        companyMail,
        AddressStreet,
        AddressLandmark,
        AddressCity,
        AddressState,
        AddressPincode,
      } = compData;
      setCompanyData({
        profilePic,
        companyName,
        GstIn,
        companyWebsite,
        phone,
        companyMail,
        address: {
          AddressStreet,
          AddressLandmark,
          AddressCity,
          AddressState,
          AddressPincode,
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getCompany();
    fetchBranches();
  }, []);

  useEffect(() => {
    getLoansData();
  }, [page, loanTenure, datePickerM, endDateR, fullReport, selectedBranch]);

  useEffect(() => {
    if (!loanData?.length) {
      setSortedData([]);
      return;
    }
    let arr = [...loanData];
    if (sortConfig.key) {
      arr.sort((a, b) => {
        const aVal =
          sortConfig.key === "firstName"
            ? a?.customer?.firstName
            : sortConfig.key === "sourceName"
              ? a?.customer?.user?.firstName
              : a[sortConfig.key];
        const bVal =
          sortConfig.key === "firstName"
            ? b?.customer?.firstName
            : sortConfig.key === "sourceName"
              ? b?.customer?.user?.firstName
              : b[sortConfig.key];

        if (sortConfig.key === "date") {
          return sortConfig.direction === "asc"
            ? +moment(aVal, "DD/MM/YYYY") - +moment(bVal, "DD/MM/YYYY")
            : +moment(bVal, "DD/MM/YYYY") - +moment(aVal, "DD/MM/YYYY");
        }
        if (typeof aVal === "number" && typeof bVal === "number")
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        if (typeof aVal === "string" && typeof bVal === "string")
          return sortConfig.direction === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        return 0;
      });
    }
    setSortedData(arr);
  }, [sortConfig, loanData]);

  useEffect(() => {
    if (!gotFullData || !sortedData.length) return;

    if (exportType === "pdf") {
      PdfReportRejection({
        sortedData,
        totalAmtR: sumLoanAmt,
        loanTenure,
        companyData,
      });
    }

    if (exportType === "excel") {
      const rows = sortedData.map((item) => ({
        "Loan ID": item.loanId,
        Borrower:
          `${item?.customer?.firstName ?? ""} ${item?.customer?.lastName ?? ""}`.trim() +
          (item?.customer?.cust_id ? ` (${item.customer.cust_id})` : ""),
        "Lent Amount": item.loanAmount,
        "Rejection Date": item.updatedAt
          ? moment(item.updatedAt).format("DD/MM/YYYY")
          : "",
        "Rejection Reason": item.cancellationReason ?? "",
        "Source Name": item?.user
          ? `${item.user.firstName} ${item.user.lastName}`
          : "",
      }));
      ExcelReport({
        data: rows,
        fileName: "rejection_report.xlsx",
        sheetName: "Rejection Report",
        columnWidths,
      });
    }

    setTimeout(() => {
      setExportType(null);
      setFullReportSync(false);
      setGotFullData(false);
    }, 100);
  }, [gotFullData]);

  const handleSortNew = (key) =>
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const handlePdfClick = () => {
    setExportType("pdf");
    setFullReportSync(true);
  };
  const handleExcelClick = () => {
    setExportType("excel");
    setFullReportSync(true);
  };

  const handleCustomDateRange = (details) => {
    if (details) {
      const dateRange = Object.values(details);
      setStartDateR(moment(dateRange[0]).format("DD MMM YYYY HH:mm:ss"));
      setEndDateR(moment(dateRange[1]).format("DD MMM YYYY HH:mm:ss"));
      setDatePickerM(false);
    }
  };

  const handleReset = () => {
    setStartDateR(null);
    setEndDateR(null);
    setLoanTenure("All");
    setSelectedBranch(null);
    setDatePickerM(false);
    setAnchorElFilter(null);
  };

  const SequenceFilter = [
    { value: "onetime", label: "Onetime" },
    { value: "daily", label: "Daily" },
    ...(getSecureItem("loggedinUserCompany") == 2891
      ? [{ value: "ten_daily", label: "Ten_Daily" }]
      : []),
    { value: "weekly", label: "Weekly" },
    { value: "biweekly", label: "Bi-Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "All", label: "All" },
  ];

  return (
    <>
      <Sidebar />
      <div style={{ marginLeft: "73px", height: "100vh" }}>
        {loading ? (
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 10 }}
          >
            <CircularProgress />
          </div>
        ) : (
          <Grid container direction="column">
            <Grid item sx={{ backgroundColor: "#fff" }}>
              <div style={{ margin: 5 }}>
                <img src={companylogo} width={200} height={40} alt="logo" />
              </div>
            </Grid>

            <strong
              style={{
                textAlign: "center",
                fontFamily: "Lato",
                marginTop: 10,
                fontSize: 18,
              }}
            >
              Rejection Report
            </strong>

            <Grid
              item
              container
              paddingX={4}
              paddingY={2}
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Card
                  elevation={3}
                  sx={{ borderRadius: 3, backgroundColor: "#fff" }}
                >
                  <CardHeader
                    title={
                      <div style={{ fontSize: 20 }}>
                        {sumLoanAmt} {currency}
                      </div>
                    }
                  />
                  <CardContent style={{ padding: 5 }}>
                    <CardHeader
                      style={{ padding: 5 }}
                      title={
                        <div style={{ fontSize: 15 }}>Loan Amount Rejected</div>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Select
                  options={branchData}
                  isDisabled={loggedinUser?.company?.planId != 3}
                  value={selectedBranch}
                  onChange={(val) => setSelectedBranch(val)}
                  placeholder="Select Branch"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={3}>
                <ToggleButtonGroup
                  color="primary"
                  value={loanTenure}
                  exclusive
                  onChange={(_, val) => {
                    setPage(1);
                    setLoanTenure(val);
                  }}
                  size="small"
                >
                  {SequenceFilter.map((f, i) => (
                    <ToggleButton key={i} value={f.value}>
                      {f.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>

              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Button
                  id="filter-button"
                  onClick={(e) => setAnchorElFilter(e.currentTarget)}
                  variant="outlined"
                  endIcon={<FilterAlt />}
                  aria-controls={openFilter ? "filter-basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={openFilter ? "true" : undefined}
                >
                  Filter
                </Button>
              </Grid>

              <Menu
                id="filter-basic-menu"
                anchorEl={anchorElFilter}
                open={openFilter}
                onClose={() => setAnchorElFilter(null)}
                MenuListProps={{
                  "aria-labelledby": "filter-button",
                  style: {
                    backgroundColor: "#2b3d63",
                    color: "#fff",
                    boxShadow: "#122344 1px 0px 5px 1px",
                  },
                }}
              >
                <MenuItem
                  onClick={() => {
                    setDatePickerM(true);
                    setAnchorElFilter(null);
                  }}
                >
                  Custom Date Range
                </MenuItem>
                <MenuItem onClick={handleReset}>Reset</MenuItem>
              </Menu>
            </Grid>

            {!loanId && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box paddingX={4} paddingY={2}>
                  <p className="Fonts">NOTE: Click on record to see details</p>
                </Box>
                <Box paddingX={4} paddingY={2}>
                  <Stack direction="row" alignItems="center" gap={2}>
                    <p className="Fonts">Download Reports:</p>
                    <img
                      src={pdf}
                      alt="pdf"
                      className="pdficon"
                      onClick={handlePdfClick}
                    />
                    <img
                      src={excel}
                      alt="excel"
                      className="excelicon"
                      onClick={handleExcelClick}
                    />
                  </Stack>
                </Box>
              </div>
            )}

            <Grid item container paddingX={4} paddingY={2}>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 700 }}>
                  <TableHead>
                    <TableRow>
                      {Object.keys(loanTableHeader).map((property) => (
                        <StyledTableCell key={property}>
                          <TableSortLabel
                            active={sortConfig.key === property}
                            direction={
                              sortConfig.key === property
                                ? sortConfig.direction
                                : "asc"
                            }
                            onClick={() => handleSortNew(property)}
                          >
                            {loanTableHeader[property]}
                          </TableSortLabel>
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedData.map((data, index) => (
                      <StyledTableRow
                        key={index}
                        onClick={() => {
                          setLoanId(data?.loanId);
                          setSelectedBorrower(
                            `${data?.customer?.firstName} ${data?.customer?.lastName}`,
                          );
                          getLoanCollections(data?.loanId);
                          setCollectionModal(true);
                        }}
                      >
                        <StyledTableCell>{data?.loanId}</StyledTableCell>
                        <StyledTableCell>
                          <Stack>
                            <div>
                              {data?.customer?.firstName}{" "}
                              {data?.customer?.lastName}
                            </div>
                            {data?.customer?.cust_id && (
                              <div>({data.customer.cust_id})</div>
                            )}
                          </Stack>
                        </StyledTableCell>
                        <StyledTableCell>{data?.loanAmount}</StyledTableCell>
                        <StyledTableCell>
                          {data?.updatedAt
                            ? moment(data.updatedAt).format("DD/MM/YYYY")
                            : ""}
                        </StyledTableCell>
                        <StyledTableCell>
                          {data?.cancellationReason ?? ""}
                        </StyledTableCell>
                        <StyledTableCell>
                          {data?.user
                            ? `${data.user.firstName} ${data.user.lastName}`
                            : ""}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                variant="outlined"
                shape="rounded"
                size="large"
                style={{ marginTop: 10 }}
              />
            </Grid>
          </Grid>
        )}
      </div>

      <Dialog
        open={collectionModal}
        fullWidth
        maxWidth="lg"
        onClose={() => {
          setLoanId(null);
          setCollectionModal(false);
        }}
        aria-labelledby="collection-dialog-title"
      >
        <DialogTitle id="collection-dialog-title">
          {`Loan Collection Table | Borrower's Name: ${selectedBorrower}, Loan ID: ${loanId}`}
        </DialogTitle>
        <DialogContent>
          {loanId && (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700 }}>
                <TableHead>
                  <TableRow>
                    {Object.keys(collectionTableHeader).map((property) => (
                      <StyledTableCell key={property}>
                        <TableSortLabel
                          active={sortConfig.key === property}
                          direction={
                            sortConfig.key === property
                              ? sortConfig.direction
                              : "asc"
                          }
                          onClick={() => handleSortNew(property)}
                        >
                          {collectionTableHeader[property]}
                        </TableSortLabel>
                      </StyledTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loanCollectionData.map((data, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      <StyledTableCell>
                        {data?.dueDate
                          ? moment(data.dueDate).format("DD/MM/YYYY")
                          : ""}
                      </StyledTableCell>
                      <StyledTableCell>{data?.openingBalance}</StyledTableCell>
                      <StyledTableCell>{data?.Installment}</StyledTableCell>
                      <StyledTableCell>{data?.closingBalance}</StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setLoanId(null);
              setCollectionModal(false);
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Customdaterange
        open={datePickerM}
        close={() => setDatePickerM(false)}
        isOnlyPast={true}
        save={handleCustomDateRange}
      />
    </>
  );
};

export default withNamespaces()(RejectionReport);
